import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from "../LanguageContext";

export default function SelectDate({ type, onDateRangeChange }) {
  const { t, lang } = useTranslation();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRange, setSelectedRange] = useState('today'); 
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    const months = t('date.months_short');
    // Check if months is an array, if not use default
    const monthsArray = Array.isArray(months) ? months : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${monthsArray[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getRangeLabel = () => {
    if (selectedRange === 'custom') {
      if (!startDate || !endDate) return t('date.select_period');
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    switch(selectedRange) {
      case 'today': return t('date.today');
      case 'this_month': return t('date.this_month');
      case 'last_30': return t('date.last_30_days');
      case 'last_6': return t('date.last_6_months');
      case 'last_12': return t('date.last_12_months');
      case 'all': return t('date.all_period');
      default: return t('date.select_period');
    }
  };

  useEffect(() => {
    handleQuickFilter('today'); 
  }, []);

  const handleQuickFilter = (filter) => {
    setSelectedRange(filter);

    if (filter === 'custom') {
      setStartDate(null);
      setEndDate(null);
      return; 
    }
    
    const today = new Date();
    let start, end;

    switch(filter) {
      case 'today':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_30':
        start = new Date();
        start.setDate(today.getDate() - 30);
        end = today;
      break;
      case 'last_6':
        start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0); 
        break;
      case 'last_12':
        start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0); 
        break;
      case 'all':
        start = new Date(2025, 9, 1); 
        end = today;
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
    
    if (onDateRangeChange) {
      onDateRangeChange({
        start_date: formatDateForAPI(start),
        end_date: formatDateForAPI(end),
        type: filter
      });
    }
    
    setShowCalendar(false);
  };

  const handleDateClick = (day, month, year) => {
    if (selectedRange !== 'custom') return;

    const clickedDate = new Date(year, month, day);
    
    if (!startDate || (startDate && endDate)) {
      setStartDate(clickedDate);
      setEndDate(null);
    } else if (clickedDate < startDate) {
      setEndDate(startDate);
      setStartDate(clickedDate);
    } else {
      setEndDate(clickedDate);
    }
  };

  const applyDateRange = () => {
    if (!startDate || !endDate) return;
    
    const finalStartDate = startDate < endDate ? startDate : endDate;
    const finalEndDate = startDate > endDate ? startDate : endDate;
    
    setStartDate(finalStartDate);
    setEndDate(finalEndDate);
    
    if (onDateRangeChange) {
      onDateRangeChange({
        start_date: formatDateForAPI(finalStartDate),
        end_date: formatDateForAPI(finalEndDate),
        type: 'custom'
      });
    }
    
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { 
      daysInMonth: lastDay.getDate(), 
      startingDayOfWeek: firstDay.getDay(), 
      year, 
      month 
    };
  };

  const isDateInRange = (day, month, year) => {
    if (!startDate) return false;
    const date = new Date(year, month, day).setHours(0, 0, 0, 0);
    const normStart = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const normEnd = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;

    if (!endDate) return date === normStart;
    
    return date >= Math.min(normStart, normEnd) && date <= Math.max(normStart, normEnd);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + direction,
      1
    ));
  };

  const renderCalendar = (monthOffset = 0, showControls = false) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(date);
    const days = [];
    
    // Get month names and ensure they are arrays
    let monthNames = t('date.months_long');
    if (!Array.isArray(monthNames)) {
      monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    }
    
    let dayNames = t('date.days_short');
    if (!Array.isArray(dayNames)) {
      dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="text-center py-1 text-gray-400"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isInRange = isDateInRange(day, month, year);
      
      const isStart = startDate && day === startDate.getDate() && month === startDate.getMonth() && year === startDate.getFullYear();
      const isEnd = endDate && day === endDate.getDate() && month === endDate.getMonth() && year === endDate.getFullYear();
      const isSingleSelected = isStart && !endDate;

      days.push(
        <div
          key={day}
          onClick={() => selectedRange === 'custom' && handleDateClick(day, month, year)} 
          className={`text-center py-1 text-xs transition-all ${
            selectedRange === 'custom' ? 'cursor-pointer' : 'cursor-default'
          } ${
            isSingleSelected || (isStart && isEnd) ? 'bg-amber-500 text-white rounded-full font-semibold' :
            isStart ? 'bg-amber-500 text-white rounded-l-full font-semibold' :
            isEnd ? 'bg-amber-500 text-white rounded-r-full font-semibold' :
            isInRange ? 'bg-[var(--color-primary-100)] text-amber-500' :
            selectedRange === 'custom' ? 'hover:bg-gray-100 rounded-md' : ''
          }`}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          {showControls && (
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {
                lang === "ar" ?
                <ChevronRight size={20} className="text-gray-600" />
                :
                <ChevronLeft size={20} className="text-gray-600" />
              }
            </button>
          )}
          <h3 className={`text-sm font-bold text-gray-800 ${!showControls && 'mx-auto'}`}>
            {monthNames[month]} {year}
          </h3>
          {showControls && (
            <button
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {
                lang === "ar" ?
                <ChevronLeft size={20} className="text-gray-600" />
                :
                <ChevronRight size={20} className="text-gray-600" />
              }
            </button>
          )}
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className={`${lang === "ar" ? 'text-[9px]' : 'text-xs'} text-center font-semibold text-gray-500 py-1`}>
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };
  
  return (
    <div className="relative mt-3 max-xl:mt-0 sm:mt-0">
      <button
        onClick={() => setShowCalendar(!showCalendar)}
        className={`flex max-xl:w-full items-center gap-2 px-4 py-2 bg-white w-50 ${type ? 'rounded-xl' : 'rounded-xl'} cursor-pointer transition-all border border-gray-200`}
      >
        <Calendar size={15} className="text-gray-900" />
        <span className="text-xs text-gray-700 text-nowrap">{getRangeLabel()}</span>
      </button>

      {showCalendar && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowCalendar(false)}
          />
          
          {/* <div className={`${lang === "ar" ? 'left-0' : 'right-0'}  absolute top-full mt-3 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col md:flex-row min-w-xl md:max-w-lg`}> */}
          <div
            className={`
              ${lang === "ar" ? "left-0" : "right-0"}
              absolute top-full mt-3
              w-[300px] max-w-[700px]
              md:w-auto
              bg-white border-2 border-gray-200 rounded-2xl shadow-2xl
              z-50 overflow-hidden
              flex flex-col md:flex-row
            `}
          >
            <div className="border-r border-gray-100 bg-gray-50 p-4 w-full md:w-52">
              <h4 className="text-xs text-gray-500 uppercase mb-3 px-2">{t('charts.shortcuts')}</h4>
              {['today', 'this_month', 'last_30', 'last_6', 'last_12', 'all', 'custom'].map(filter => (
                <button
                  key={filter}
                  onClick={() => handleQuickFilter(filter)}
                  className={`${lang === "ar" ? 'text-right' : 'text-left'} w-full text-left px-3 py-2.5 text-xs rounded-lg transition-all mb-1 ${
                    selectedRange === filter ? 'bg-amber-500 text-white' : 'hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {filter === 'today' ? t('date.today') :
                   filter === 'this_month' ? t('date.this_month') :
                   filter === 'last_30' ? t('date.last_30_days') :
                   filter === 'last_6' ? t('date.last_6_months') :
                   filter === 'last_12' ? t('date.last_12_months') :
                   filter === 'all' ? t('date.all_period') : t('date.custom_period')}
                </button>
              ))}
            </div>
            
            <div>
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-60 border-b md:border-b-0 md:border-r border-gray-200">
                  {renderCalendar(0, true)}
                </div>
                <div className="w-full md:w-60">
                  {renderCalendar(1)}
                </div>
              </div>

              <div className="border-t border-gray-200 p-4 bg-gray-50 flex flex-col sm:flex-row justify-between items-center w-full">
                <div className="text-sm text-gray-600 font-medium mb-3 sm:mb-0">
                  {startDate && endDate ? (
                    <>
                      <span className='text-xs'>{formatDate(startDate)}</span>
                      <span className='ml-1'> - <span className='text-xs'>{formatDate(endDate)}</span></span>
                    </>
                  ) : startDate ? (
                    <span className='font-bold text-xs text-amber-500'>{formatDate(startDate)}</span>
                  ) : (
                    t('date.select_period')
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => { 
                      setShowCalendar(false); 
                    }}
                    className="px-4 py-2 text-xs border-2 cursor-pointer border-gray-300 rounded-xl hover:bg-gray-100 transition-colors font-semibold text-gray-700"
                  >
                    {t('date.cancel')}
                  </button>
                  <button
                    onClick={applyDateRange}
                    disabled={selectedRange !== 'custom' || !startDate || !endDate}
                    className={`px-4 py-2 text-xs rounded-xl font-bold transition-colors ${
                      selectedRange === 'custom' && startDate && endDate ? 'bg-amber-500 text-white hover:bg-[var(--color-primary-300)] shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {t('date.apply')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}